import { Children, cloneElement, useEffect, useRef, useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { UnstyledButton } from 'utils/fragments';
import styled from 'styled-components';
import { useTooltipInPortal } from '@visx/tooltip';
import { withMixpanel } from 'utils/app';

const TOOLTIP_Z_INDEX = 60;

export const CatFiveInfo = styled(UnstyledButton).attrs(
    ({ children = <FiHelpCircle />, ariaLabel = 'Info' }) => ({
        children,
        ariaLabel,
    }),
)`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.purple_1};
    font-size: 14px;
    user-select: none;
    padding: 0px;
    margin: 0px 3px;

    &:hover {
        color: ${({ theme }) => theme.purple_4};
    }
`;

const DefaultTooltipBox = styled.div`
    background-color: ${({ theme }) => theme.gray_1};
    color: ${({ theme }) => theme.gray_4};
    padding: ${({ theme }) => theme.space_6}px ${({ theme }) => theme.space_7}px;
    font-family: ${({ theme }) => theme.style_primary};
    font-weight: ${({ theme }) => theme.fontMedium};
    font-size: ${({ theme }) => theme.fontSize_3};
    line-height: 1.45;
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    box-shadow: ${({ theme }) => theme.box_shadow_1};
    letter-spacing: -0.01em;

    & > * {
        margin-bottom: ${({ theme }) => theme.space_3}px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    a {
        color: ${({ theme }) => theme.gray_7};
    }

    ${({ theme, icon }) =>
        icon
            ? `
              display: grid;
              grid-template-columns: minmax(${theme.space_9}px, min-content) 1fr;
              grid-column-gap: ${theme.space_6}px;
          `
            : null}
`;

function Tooltip({
    children = <CatFiveInfo />,
    content,
    width = '18.75em',
    openOnClick = false,
    icon = null,
    mixpanel,
    mixpanelContext = 'Tooltip',
    mixpanelMeta = {},
    TooltipBox = DefaultTooltipBox,
    showTimeout = 1200,
    hideTimeout = 350,
}) {
    const showTimer = useRef(null);
    const hideTimer = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipCoordinates, setTooltipCoordinates] = useState({
        x: -Infinity,
        y: -Infinity,
        xOffset: 10,
        yOffset: 10,
    });
    const { TooltipInPortal } = useTooltipInPortal({
        scroll: true,
        detectBounds: true,
    });

    useEffect(
        () => () => {
            if (hideTimer.current) clearTimeout(hideTimer.current);
            if (showTimer.current) clearTimeout(showTimer.current);
        },
        [],
    );

    const showTooltip = (timeout = showTimeout) => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        if (!isVisible)
            showTimer.current = setTimeout(() => {
                // track as a click event for continuity of event type
                mixpanel.track('Click', {
                    ...mixpanelMeta,
                    context: mixpanelContext,
                });
                setIsVisible(true);
            }, timeout);
    };

    const hideTooltip = (timeout = hideTimeout) => {
        if (showTimer.current) clearTimeout(showTimer.current);
        if (isVisible)
            hideTimer.current = setTimeout(setIsVisible, timeout, false);
    };

    const { x, y, xOffset, yOffset } = tooltipCoordinates;

    const targetProps = {
        onMouseLeave: (e) => hideTooltip(),
        onMouseMove: (e) => {
            const { left, top, width, height } =
                e.currentTarget.getBoundingClientRect();
            const halfWidth = width / 2;
            const halfHeight = height / 2;
            const nextTooltipCoordinates = {
                x: left + halfWidth,
                y: top + halfHeight,
                xOffset: halfWidth,
                yOffset: halfHeight,
            };
            if (!(
                nextTooltipCoordinates.x === x && nextTooltipCoordinates.y === y
            ))
                setTooltipCoordinates(nextTooltipCoordinates);
        },
    };

    if (openOnClick) {
        targetProps.onClick = (e) =>
            isVisible ? hideTooltip(0) : showTooltip(0);
    } else {
        targetProps.onMouseEnter = (e) => showTooltip();
    }

    return (
        <>
            {cloneElement(Children.only(children), targetProps)}
            {isVisible ? (
                <TooltipInPortal
                    style={{ position: 'absolute', zIndex: TOOLTIP_Z_INDEX }}
                    left={x}
                    top={y}
                    offsetLeft={xOffset}
                    offsetTop={yOffset}
                >
                    <TooltipBox
                        icon={!!icon}
                        width={width}
                        onMouseEnter={(e) => showTooltip()}
                        onMouseLeave={(e) => hideTooltip()}
                    >
                        {icon ? (
                            <div
                                css={`
                                    font-size: ${({ theme }) =>
                                        theme.space_9}px;
                                `}
                            >
                                {icon}
                            </div>
                        ) : null}
                        <div
                            css={`
                                width: ${width};
                            `}
                        >
                            {content || null}
                        </div>
                    </TooltipBox>
                </TooltipInPortal>
            ) : null}
        </>
    );
}

export default withMixpanel(Tooltip);
